// src/lib/managers/api/types/clientInfo.ts

/**
 * Identité du client qui appelle l'Api-Backend (F-1.24), fournie par l'app à `<MailvistaSDK>`.
 * Elle part dans l'en-tête `X-Mv-Client` de chaque appel, avec un `X-Mv-Trace-Id` par tour de
 * l'assistant : l'Api-Ai la journalise (version du client, part des appels par version, §8) et
 * l'Api-Backend pourra s'en servir pour exiger une mise à jour (426, BK-5).
 *
 * Absente, AUCUN des deux en-têtes ne part : c'est le cas du web et du desktop tant que
 * l'Api-Backend ne les autorise pas en CORS (BK-1.3), sans quoi le navigateur bloquerait tous les
 * appels.
 */
export type ClientInfo = {
    /** Plateforme : `ios`, `android`, `web`, `desktop`. */
    platform: string;
    /** Version de l'app, telle que publiée (ex. `1.2`). */
    appVersion: string;
    /**
     * Numéro de build natif (mobile) : entier qui ne fait que monter, d'une version à l'autre.
     * C'est lui que l'Api-Backend compare pour une version minimale. Vide ou absent ailleurs.
     */
    build?: string;
    /** Commit du monorepo qui a produit le build : le SDK n'a pas d'autre numéro de version. */
    sdkCommit: string;
};

/** Version du contrat entre l'app et l'Api-Ai : le contrat v1 (`toolCall` exécuté par le client). */
const CLIENT_CONTRACT_VERSION = 1;

/** Valeur mise à la place d'un champ vide, pour que l'en-tête reste analysable. */
const UNKNOWN_VALUE = "unknown";

/**
 * Un champ de l'en-tête : sans espace ni séparateur (`/`, `;`, `=`, `+`), qui casseraient sa
 * lecture côté serveur. Tout autre caractère devient `-`.
 */
const headerToken = (value: string | undefined): string => {
    const trimmed = (value ?? "").trim();
    return trimmed ? trimmed.replace(/[^A-Za-z0-9._-]/g, "-") : UNKNOWN_VALUE;
};

/**
 * Valeur de `X-Mv-Client` : `<plateforme>/<version app>; sdk=<commit>; contract=1`.
 * Sur mobile, la version porte le numéro de build après un `+` (`ios/1.2+57`), comme la
 * métadonnée de build de semver : deux builds d'une même version restent distincts.
 */
export const formatClientHeader = (clientInfo: ClientInfo): string => {
    const build = (clientInfo.build ?? "").trim();
    const version = build
        ? `${headerToken(clientInfo.appVersion)}+${headerToken(build)}`
        : headerToken(clientInfo.appVersion);

    return `${headerToken(clientInfo.platform)}/${version}; sdk=${headerToken(clientInfo.sdkCommit)}; contract=${CLIENT_CONTRACT_VERSION}`;
};
